import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { listFacilities } from '@/epics/facilityManagement/api/listFacilityApiService';
import {
  listEquipment,
  type EquipmentListItem,
} from '@/epics/facilityManagement/api/listEquipmentApiService';
import {
  fetchProfile,
  type UserProfile,
} from '@/epics/userProfile/api/viewProfileApiService';
import { getPresignedUrlsBatch } from '@/epics/facilityManagement/api/presignedUrlBatchApiService';
import { uploadFileToS3 } from '@/shared/utils/uploadFileToS3';
import { createServiceRequest } from '../../api/createServiceRequestApiService';
import { listServiceTypesApiService } from '../../api/listServiceTypesApiService';
import { useClientId } from '@/shared/hooks/useClientId';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import { useDebounce } from '@/shared/hooks/useDebounce';
import type { ServiceType } from '../../types/serviceTypeTypes';
import type { FacilityListItem } from '@/epics/facilityManagement/types/facilityListType';

export type StepNum = 1 | 2 | 3;

const FACILITY_PAGE_SIZE = 100;
const EQUIPMENT_PAGE_SIZE = 100;

export interface UseCreateServiceRequestResult {
  // Step
  step: StepNum;
  maxStep: StepNum;

  // Service type
  serviceType: ServiceType | null;
  serviceTypeLoading: boolean;
  serviceTypeError: string;

  // Client info (for preview)
  clientInfo: UserProfile | null;
  clientInfoLoading: boolean;

  // Step 1: Facility
  facilities: FacilityListItem[];
  facilitiesLoading: boolean;
  facilitiesHasNextPage: boolean;
  selectedFacilityId: string | null;
  selectedFacility: FacilityListItem | null;
  facilityError: string;
  canListFacilities: boolean;
  privilegesLoading: boolean;

  // Step 1: Equipment
  equipment: EquipmentListItem[];
  equipmentLoading: boolean;
  equipmentHasNextPage: boolean;
  selectedEquipmentId: string | null;
  selectedEquipment: EquipmentListItem | null;
  canListEquipment: boolean;
  isEquipmentLoadingForFacility: boolean;

  // Step 2: Description
  description: string;
  descriptionError: string;
  step2ValidationAttempt: number;

  // Step 2: Files
  images: File[];
  imageUploadProgress: Record<string, number>;
  imagesError: string;
  document: File | null;
  documentUploadProgress: Record<string, number>;
  documentError: string;

  // Submit
  isUploading: boolean;
  isSubmitting: boolean;
  submitError: string;

  // Cancel modal
  isCancelModalOpen: boolean;

  // Handlers
  handleFacilitySelect: (id: string) => void;
  handleFacilitySearchChange: (query: string) => void;
  handleEquipmentSelect: (id: string) => void;
  handleEquipmentSearchChange: (query: string) => void;
  handleDescriptionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleImagesSelect: (files: File[]) => void;
  handleImagesError: (message: string) => void;
  handleDocumentSelect: (files: File[]) => void;
  handleDocumentError: (message: string) => void;
  handleFacilitiesScrollEnd: () => void;
  handleEquipmentScrollEnd: () => void;
  handleStepClick: (targetStep: StepNum) => void;
  handleNext: () => void;
  handleBack: () => void;
  handleCancel: () => void;
  handleCloseCancelModal: () => void;
  handleConfirmCancel: () => void;
  handleSubmit: () => void;
  handleCloseSubmitError: () => void;
}

export function useCreateServiceRequest(): UseCreateServiceRequestResult {
  const { serviceTypeName } = useParams<{ serviceTypeName: string }>();
  const navigate = useNavigate();
  const { clientId, isLoading: clientIdLoading } = useClientId();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  const [step, setStep] = useState<StepNum>(1);
  const [maxStep, setMaxStep] = useState<StepNum>(1);

  // Service type
  const [serviceType, setServiceType] = useState<ServiceType | null>(null);
  const [serviceTypeLoading, setServiceTypeLoading] = useState(true);
  const [serviceTypeError, setServiceTypeError] = useState('');

  // Client info
  const [clientInfo, setClientInfo] = useState<UserProfile | null>(null);
  const [clientInfoLoading, setClientInfoLoading] = useState(true);

  // Facilities
  const [facilities, setFacilities] = useState<FacilityListItem[]>([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [facilitiesHasNextPage, setFacilitiesHasNextPage] = useState(false);
  const [facilitySearchQuery, setFacilitySearchQuery] = useState('');
  const debouncedFacilitySearchQuery = useDebounce(facilitySearchQuery, 500);
  const facilitiesLoadingRef = useRef(false);
  const facilitiesPageRef = useRef(1);

  // Equipment
  const [equipment, setEquipment] = useState<EquipmentListItem[]>([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [equipmentHasNextPage, setEquipmentHasNextPage] = useState(false);
  const [isEquipmentLoadingForFacility, setIsEquipmentLoadingForFacility] =
    useState(false);
  const [equipmentSearchQuery, setEquipmentSearchQuery] = useState('');
  const debouncedEquipmentSearchQuery = useDebounce(equipmentSearchQuery, 500);
  const equipmentLoadingRef = useRef(false);
  const equipmentPageRef = useRef(1);

  // Step 1 state
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(
    null
  );
  const [selectedFacility, setSelectedFacility] =
    useState<FacilityListItem | null>(null);
  const [facilityError, setFacilityError] = useState('');
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(
    null
  );
  const [selectedEquipment, setSelectedEquipment] =
    useState<EquipmentListItem | null>(null);

  // Step 2 state
  const [description, setDescription] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imageUploadProgress, setImageUploadProgress] = useState<
    Record<string, number>
  >({});
  const [imagesError, setImagesError] = useState('');
  const [document, setDocument] = useState<File | null>(null);
  const [documentUploadProgress, setDocumentUploadProgress] = useState<
    Record<string, number>
  >({});
  const [documentError, setDocumentError] = useState('');
  const [step2ValidationAttempt, setStep2ValidationAttempt] = useState(0);

  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Cancel modal state
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Fake progress timeout tracking
  const progressTimeoutsRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});

  // Cleanup timeouts on unmount
  useEffect(() => {
    const timeouts = progressTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach(clearTimeout);
    };
  }, []);

  // Fetch service type
  useEffect(() => {
    if (!serviceTypeName) {
      setServiceTypeLoading(false);
      setServiceType(null);
      setServiceTypeError('Nie udało się załadować danych usługi.');
      return;
    }

    setServiceTypeLoading(true);
    const decodedServiceTypeName = decodeURIComponent(serviceTypeName);

    listServiceTypesApiService
      .listServiceTypes({
        search: decodedServiceTypeName,
        page: 1,
        limit: 10,
      })
      .then((result) => {
        const matchedServiceType =
          result.data.find(
            (item) => item.service_name === decodedServiceTypeName
          ) ?? result.data[0];

        if (!matchedServiceType) {
          setServiceType(null);
          setServiceTypeError('Nie udało się załadować danych usługi.');
          return;
        }

        setServiceType({
          id: matchedServiceType.id,
          service_name: matchedServiceType.service_name,
          description: matchedServiceType.description,
          status: matchedServiceType.status,
          created_at: matchedServiceType.created_at,
          updated_at: matchedServiceType.created_at,
          created_by: null,
          updated_by: null,
        });
        setServiceTypeError('');
      })
      .catch(() => {
        setServiceType(null);
        setServiceTypeError('Nie udało się załadować danych usługi.');
      })
      .finally(() => {
        setServiceTypeLoading(false);
      });
  }, [serviceTypeName]);

  // Fetch client info via profile endpoint (accessible to all roles)
  useEffect(() => {
    setClientInfoLoading(true);
    fetchProfile().then((result) => {
      if (result.success && result.data) {
        setClientInfo(result.data);
      }
      setClientInfoLoading(false);
    });
  }, []);

  // ─── Paginated facilities fetch ───────────────────────────────────────────────

  const fetchFacilitiesPage = useCallback(
    async (cId: string, page: number, search = '') => {
      if (facilitiesLoadingRef.current) {
        return;
      }
      facilitiesLoadingRef.current = true;
      setFacilitiesLoading(true);

      const result = await listFacilities(cId, {
        status: 'active',
        page,
        limit: FACILITY_PAGE_SIZE,
        ...(search.trim() ? { search: search.trim() } : {}),
      });

      facilitiesLoadingRef.current = false;
      setFacilitiesLoading(false);

      if (result.success && result.facilities) {
        const facilities = result.facilities;
        setFacilities((prev) =>
          page === 1 ? facilities : [...prev, ...facilities]
        );
        const hasNext = (result.totalCount ?? 0) > page * FACILITY_PAGE_SIZE;
        setFacilitiesHasNextPage(hasNext);
        facilitiesPageRef.current = page;
      } else if (page === 1) {
        setFacilities([]);
        setFacilitiesHasNextPage(false);
      }
    },
    []
  );

  useEffect(() => {
    if (clientIdLoading || !clientId) {
      return;
    }
    facilitiesPageRef.current = 1;
    facilitiesLoadingRef.current = false;
    setFacilities([]);
    setFacilitiesHasNextPage(false);
    fetchFacilitiesPage(clientId, 1, debouncedFacilitySearchQuery);
  }, [
    clientId,
    clientIdLoading,
    debouncedFacilitySearchQuery,
    fetchFacilitiesPage,
  ]);

  const handleFacilitiesScrollEnd = useCallback(() => {
    if (facilitiesLoadingRef.current || !facilitiesHasNextPage || !clientId) {
      return;
    }
    fetchFacilitiesPage(
      clientId,
      facilitiesPageRef.current + 1,
      debouncedFacilitySearchQuery
    );
  }, [
    facilitiesHasNextPage,
    clientId,
    debouncedFacilitySearchQuery,
    fetchFacilitiesPage,
  ]);

  // ─── Paginated equipment fetch ────────────────────────────────────────────────

  const fetchEquipmentPage = useCallback(
    async (cId: string, facilityId: string, page: number, search = '') => {
      if (equipmentLoadingRef.current) {
        return;
      }
      equipmentLoadingRef.current = true;
      setEquipmentLoading(true);

      const result = await listEquipment({
        clientId: cId,
        facilityId,
        page,
        limit: EQUIPMENT_PAGE_SIZE,
        ...(search.trim() ? { search: search.trim() } : {}),
      });

      equipmentLoadingRef.current = false;
      setEquipmentLoading(false);
      setIsEquipmentLoadingForFacility(false);

      if (result.success && result.data) {
        const { equipment: equipmentData } = result.data;
        setEquipment((prev) =>
          page === 1 ? equipmentData : [...prev, ...equipmentData]
        );
        setEquipmentHasNextPage(result.data.pagination.hasNextPage);
        equipmentPageRef.current = page;
      } else if (page === 1) {
        setEquipment([]);
      }
    },
    []
  );

  useEffect(() => {
    if (!selectedFacility?.id || !clientId) {
      setEquipment([]);
      setEquipmentHasNextPage(false);
      setSelectedEquipmentId(null);
      setSelectedEquipment(null);
      setEquipmentSearchQuery('');
      return;
    }
    equipmentPageRef.current = 1;
    equipmentLoadingRef.current = false;
    setEquipment([]);
    setEquipmentHasNextPage(false);
    fetchEquipmentPage(
      clientId,
      selectedFacility.id,
      1,
      debouncedEquipmentSearchQuery
    );
  }, [
    selectedFacility,
    clientId,
    debouncedEquipmentSearchQuery,
    fetchEquipmentPage,
  ]);

  const handleEquipmentScrollEnd = useCallback(() => {
    if (
      equipmentLoadingRef.current ||
      !equipmentHasNextPage ||
      !clientId ||
      !selectedFacility?.id
    ) {
      return;
    }
    fetchEquipmentPage(
      clientId,
      selectedFacility.id,
      equipmentPageRef.current + 1,
      debouncedEquipmentSearchQuery
    );
  }, [
    equipmentHasNextPage,
    clientId,
    selectedFacility,
    debouncedEquipmentSearchQuery,
    fetchEquipmentPage,
  ]);

  // ─── Fake upload progress ─────────────────────────────────────────────────────

  const runFakeProgress = useCallback(
    (
      filename: string,
      fileSize: number,
      setter: React.Dispatch<React.SetStateAction<Record<string, number>>>
    ) => {
      // Steps are percentages; convert to bytes so FileUpload's (progress / file.size) formula works correctly
      const percentSteps = [20, 45, 65, 80, 95, 100];
      const steps = percentSteps.map((p) => Math.round((p / 100) * fileSize));
      let stepIdx = 0;

      const tick = () => {
        if (stepIdx >= steps.length) {
          return;
        }
        const value = steps[stepIdx++];
        if (value === undefined) {
          return;
        }
        setter((prev) => ({ ...prev, [filename]: value }));
        if (value < fileSize) {
          progressTimeoutsRef.current[filename] = setTimeout(tick, 250);
        } else {
          progressTimeoutsRef.current[filename] = setTimeout(() => {
            setter((prev) => {
              const { [filename]: _, ...next } = prev;
              return next;
            });
            delete progressTimeoutsRef.current[filename];
          }, 300);
        }
      };

      progressTimeoutsRef.current[filename] = setTimeout(tick, 100);
    },
    []
  );

  const cancelFakeProgress = useCallback(
    (
      filename: string,
      setter: React.Dispatch<React.SetStateAction<Record<string, number>>>
    ) => {
      clearTimeout(progressTimeoutsRef.current[filename]);
      delete progressTimeoutsRef.current[filename];
      setter((prev) => {
        const next = { ...prev };
        delete next[filename];
        return next;
      });
    },
    []
  );

  const isUploading =
    Object.keys(imageUploadProgress).length > 0 ||
    Object.keys(documentUploadProgress).length > 0;

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  const handleFacilitySelect = useCallback(
    (id: string) => {
      const facility = facilities.find((f) => f.id === id) ?? null;
      setSelectedFacilityId(id);
      setSelectedFacility(facility);
      setFacilityError('');
      setSelectedEquipmentId(null);
      setSelectedEquipment(null);
      setIsEquipmentLoadingForFacility(true);
    },
    [facilities]
  );

  const handleFacilitySearchChange = useCallback(
    (query: string) => {
      if (query === facilitySearchQuery) {
        return;
      }
      facilitiesPageRef.current = 1;
      facilitiesLoadingRef.current = false;
      setFacilities([]);
      setFacilitiesLoading(true);
      setFacilitiesHasNextPage(false);
      setFacilitySearchQuery(query);
    },
    [facilitySearchQuery]
  );

  const handleEquipmentSelect = useCallback(
    (id: string) => {
      const equip = equipment.find((e) => e.id === id) ?? null;
      setSelectedEquipmentId(id);
      setSelectedEquipment(equip);
    },
    [equipment]
  );

  const handleEquipmentSearchChange = useCallback(
    (query: string) => {
      if (query === equipmentSearchQuery) {
        return;
      }
      equipmentPageRef.current = 1;
      equipmentLoadingRef.current = false;
      setEquipment([]);
      setEquipmentLoading(true);
      setEquipmentHasNextPage(false);
      setEquipmentSearchQuery(query);
    },
    [equipmentSearchQuery]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value.trimStart();
      setDescription(val);
      if (val.trim()) {
        setDescriptionError('');
      }
    },
    []
  );

  const handleImagesSelect = useCallback(
    (updatedFiles: File[]) => {
      const currentNames = new Set(images.map((f) => f.name));
      const newFiles = updatedFiles.filter((f) => !currentNames.has(f.name));
      const removedNames = [...currentNames].filter(
        (n) => !updatedFiles.some((f) => f.name === n)
      );

      newFiles.forEach((f) =>
        runFakeProgress(f.name, f.size, setImageUploadProgress)
      );
      removedNames.forEach((n) =>
        cancelFakeProgress(n, setImageUploadProgress)
      );

      setImages(updatedFiles);
      if (updatedFiles.length > 0) {
        setImagesError('');
      }
    },
    [images, runFakeProgress, cancelFakeProgress]
  );

  const handleImagesError = useCallback((message: string) => {
    setImagesError(message);
  }, []);

  const handleDocumentSelect = useCallback(
    (updatedFiles: File[]) => {
      const prev = document;
      const newFile = updatedFiles[0] ?? null;

      if (prev && (!newFile || newFile.name !== prev.name)) {
        cancelFakeProgress(prev.name, setDocumentUploadProgress);
      }
      if (newFile && (!prev || newFile.name !== prev.name)) {
        runFakeProgress(newFile.name, newFile.size, setDocumentUploadProgress);
      }

      setDocument(newFile);
      if (newFile) {
        setDocumentError('');
      }
    },
    [document, runFakeProgress, cancelFakeProgress]
  );

  const handleDocumentError = useCallback((message: string) => {
    setDocumentError(message);
  }, []);

  const handleStepClick = useCallback(
    (targetStep: StepNum) => {
      if (targetStep === step) {
        return;
      }
      if (step === 2 && targetStep > step && isUploading) {
        return;
      }

      if (targetStep === 3) {
        if (!selectedFacilityId) {
          setFacilityError('To pole jest wymagane');
          return;
        }
        if (!description.trim()) {
          setDescriptionError('To pole jest wymagane');
          if (step === 2) {
            setStep2ValidationAttempt((prev) => prev + 1);
          }
          return;
        }
        if (images.length === 0) {
          setImagesError('To pole jest wymagane');
          if (step === 2) {
            setStep2ValidationAttempt((prev) => prev + 1);
          }
          return;
        }
        if (!document) {
          setDocumentError('To pole jest wymagane');
          if (step === 2) {
            setStep2ValidationAttempt((prev) => prev + 1);
          }
          return;
        }
        setStep(3);
        setMaxStep((prev) => Math.max(prev, 3) as StepNum);
        return;
      }

      if (targetStep > maxStep) {
        return;
      }

      if (targetStep > step) {
        if (step === 1 && !selectedFacilityId) {
          setFacilityError('To pole jest wymagane');
          return;
        }
        if (step === 2) {
          let hasError = false;
          if (!description.trim()) {
            setDescriptionError('To pole jest wymagane');
            hasError = true;
          }
          if (images.length === 0) {
            setImagesError('To pole jest wymagane');
            hasError = true;
          }
          if (!document) {
            setDocumentError('To pole jest wymagane');
            hasError = true;
          }
          if (hasError) {
            setStep2ValidationAttempt((prev) => prev + 1);
            return;
          }
        }
      }

      setStep(targetStep);
    },
    [
      step,
      maxStep,
      isUploading,
      selectedFacilityId,
      description,
      images,
      document,
    ]
  );

  const handleNext = useCallback(() => {
    if (step === 2 && isUploading) {
      return;
    }
    if (step === 1) {
      if (!selectedFacilityId) {
        setFacilityError('To pole jest wymagane');
        return;
      }
      setStep(2);
      setMaxStep((prev) => Math.max(prev, 2) as StepNum);
    } else if (step === 2) {
      let hasError = false;
      if (!description.trim()) {
        setDescriptionError('To pole jest wymagane');
        hasError = true;
      }
      if (images.length === 0) {
        setImagesError('To pole jest wymagane');
        hasError = true;
      }
      if (!document) {
        setDocumentError('To pole jest wymagane');
        hasError = true;
      }
      if (hasError) {
        setStep2ValidationAttempt((prev) => prev + 1);
        return;
      }
      setStep(3);
      setMaxStep((prev) => Math.max(prev, 3) as StepNum);
    }
  }, [step, isUploading, selectedFacilityId, description, images, document]);

  const handleBack = useCallback(() => {
    if (step === 2) {
      setDescriptionError('');
      setStep(1);
    } else if (step === 3) {
      setStep(2);
    }
  }, [step]);

  const handleCancel = useCallback(() => {
    const isDirty = Boolean(
      selectedFacilityId ||
        selectedEquipmentId ||
        description.trim() ||
        images.length > 0 ||
        document
    );
    if (isDirty) {
      setIsCancelModalOpen(true);
      return;
    }
    navigate('/Produkty i usługi');
  }, [
    navigate,
    selectedFacilityId,
    selectedEquipmentId,
    description,
    images,
    document,
  ]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    navigate('/Produkty i usługi');
  }, [navigate]);

  const handleSubmit = useCallback(async () => {
    if (!serviceType?.id || !selectedFacilityId) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const uploadedImages: {
        isUploaded: boolean;
        path: string;
        resourceId: string;
        filename: string;
        fileType: string;
        fileSize: number;
      }[] = [];
      let uploadedDocument:
        | {
            isUploaded: boolean;
            path: string;
            resourceId: string;
            filename: string;
            fileType: string;
            fileSize: number;
          }
        | undefined = undefined;

      const ENTITY_TYPE = 'service-requests';

      for (const img of images) {
        const presignedResult = await getPresignedUrlsBatch({
          paths: [
            `${ENTITY_TYPE}/entityid-placeholder/images/resourceid-placeholder/${img.name}`,
          ],
          generatePath: true,
        });
        const presigned = presignedResult.files[0];
        if (!presigned) {
          throw new Error(`Missing presigned URL for ${img.name}`);
        }
        await uploadFileToS3(presigned.url, img);
        uploadedImages.push({
          isUploaded: true,
          path: presigned.path,
          resourceId: presigned.resourceId,
          filename: img.name,
          fileType: img.type,
          fileSize: img.size,
        });
      }

      if (document) {
        const presignedResult = await getPresignedUrlsBatch({
          paths: [
            `${ENTITY_TYPE}/entityid-placeholder/docs/resourceid-placeholder/${document.name}`,
          ],
          generatePath: true,
        });
        const presigned = presignedResult.files[0];
        if (!presigned) {
          throw new Error(`Missing presigned URL for ${document.name}`);
        }
        await uploadFileToS3(presigned.url, document);
        uploadedDocument = {
          isUploaded: true,
          path: presigned.path,
          resourceId: presigned.resourceId,
          filename: document.name,
          fileType: document.type,
          fileSize: document.size,
        };
      }

      const result = await createServiceRequest({
        service_type_id: serviceType.id,
        facility_id: selectedFacilityId,
        ...(selectedEquipmentId ? { equipment_id: selectedEquipmentId } : {}),
        description,
        ...(uploadedImages.length > 0 ? { images: uploadedImages } : {}),
        ...(uploadedDocument ? { document: uploadedDocument } : {}),
      });

      if (result.success) {
        navigate('/Produkty i usługi?tab=my-requests', {
          state: {
            navigationSuccessMessage:
              'Twoje zgłoszenie serwisowe zostało pomyślnie wysłane.',
          },
        });
      } else {
        setSubmitError(
          result.error ??
            'Nie udało się złożyć zgłoszenia serwisowego. Spróbuj ponownie.'
        );
      }
    } catch {
      setSubmitError(
        'Nie udało się złożyć zgłoszenia serwisowego. Spróbuj ponownie.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    serviceType,
    selectedFacilityId,
    selectedEquipmentId,
    description,
    images,
    document,
    navigate,
  ]);

  const handleCloseSubmitError = useCallback(() => setSubmitError(''), []);

  return {
    step,
    maxStep,
    serviceType,
    serviceTypeLoading,
    serviceTypeError,
    clientInfo,
    clientInfoLoading,
    facilities,
    facilitiesLoading,
    facilitiesHasNextPage,
    selectedFacilityId,
    selectedFacility,
    facilityError,
    canListFacilities: privileges.canListFacilities,
    privilegesLoading,
    equipment,
    equipmentLoading,
    equipmentHasNextPage,
    selectedEquipmentId,
    selectedEquipment,
    canListEquipment: privileges.canListEquipment,
    isEquipmentLoadingForFacility,
    description,
    descriptionError,
    step2ValidationAttempt,
    images,
    imageUploadProgress,
    imagesError,
    document,
    documentUploadProgress,
    documentError,
    isUploading,
    isSubmitting,
    submitError,
    handleFacilitySelect,
    handleFacilitySearchChange,
    handleEquipmentSelect,
    handleEquipmentSearchChange,
    handleDescriptionChange,
    handleImagesSelect,
    handleImagesError,
    handleDocumentSelect,
    handleDocumentError,
    handleFacilitiesScrollEnd,
    handleEquipmentScrollEnd,
    handleStepClick,
    handleNext,
    handleBack,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleSubmit,
    handleCloseSubmitError,
    isCancelModalOpen,
  };
}
